import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Badge, Alert, Table, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney } from '../lib/format.js';
import { groupFeaturesIntoBundles } from '../lib/featureBundles.js';

const PERIOD_LABEL = { one_time: 'einmalig', monthly: 'monatlich', yearly: 'jährlich' };

/**
 * Berechnet client-seitig den Preis für ein Feature anhand seiner mitgelieferten
 * Tiers. Server bleibt Source-of-Truth (Submit re-validiert), aber wir wollen
 * eine snappy Live-Anzeige während der User die Quantity hin- und herbewegt.
 */
function priceFor(feature, qty) {
  if (!feature) return 0;
  switch (feature.pricing_model) {
    case 'free':     return 0;
    case 'flat':     return feature.price_cents || 0;
    case 'per_unit': return (feature.price_cents || 0) * qty;
    case 'tiered': {
      const tiers = feature.tiers || [];
      const tier = tiers.find(t => qty >= t.min_qty && (t.max_qty == null || qty <= t.max_qty));
      if (tier) return tier.unit_price_cents * qty;
      const last = tiers[tiers.length - 1];
      return last ? last.unit_price_cents * qty : 0;
    }
    default: return 0;
  }
}

function defaultQty(feature) {
  if (feature.feature_type === 'quota')   return feature.default_value?.limit || 1;
  return 1;
}

function renderFeatureRow(f, selected, setQty, toggleFeature, { isChild = false } = {}) {
  const sel = selected[f.id];
  const checked = !!sel;
  const qty = checked ? Math.max(1, parseInt(sel.quantity, 10) || 1) : defaultQty(f);
  const showQty = checked && (f.pricing_model === 'per_unit' || f.pricing_model === 'tiered' || f.feature_type === 'quota');
  const linePrice = checked ? priceFor(f, qty) : priceFor(f, defaultQty(f));
  return (
    <tr key={f.id} className={checked ? 'table-active' : ''}>
      <td style={{ width: 40, paddingLeft: isChild ? '2rem' : undefined }}>
        <Form.Check checked={checked} onChange={() => toggleFeature(f)} />
      </td>
      <td>
        <div className="fw-semibold">{f.name}</div>
        {f.description && <div className="text-muted small">{f.description}</div>}
        <div className="small mt-1">
          <Badge bg="" className="badge-soft-secondary me-1">{f.feature_type}</Badge>
          {f.pricing_model !== 'free' && (
            <Badge bg="" className="badge-soft-info">
              {f.pricing_model === 'flat'     && `${fmtMoney(f.price_cents)} ${PERIOD_LABEL[f.billing_period]}`}
              {f.pricing_model === 'per_unit' && `${fmtMoney(f.price_cents)} pro Einheit ${PERIOD_LABEL[f.billing_period]}`}
              {f.pricing_model === 'tiered'   && `gestaffelt (${(f.tiers || []).length} Stufen)`}
            </Badge>
          )}
          {f.pricing_model === 'free' && <span className="text-muted small">— kostenlos</span>}
        </div>
      </td>
      <td style={{ width: 140 }}>
        {showQty && (
          <Form.Control size="sm" type="number" min="1" value={qty}
            onChange={(e) => setQty(f.id, e.target.value)} />
        )}
      </td>
      <td className="text-end" style={{ width: 130 }}>
        {checked && f.pricing_model !== 'free' && (
          <div>
            <div className="fw-semibold">{fmtMoney(linePrice)}</div>
            <div className="text-muted small">{PERIOD_LABEL[f.billing_period]}</div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function LicenseConfiguratorPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const customers = useQuery({
    queryKey: ['customers-min'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });
  const features = useQuery({
    queryKey: ['features-with-tiers'],
    queryFn: () => api.get('/features', { params: { with_tiers: true } }).then(r => r.data.items)
  });

  // ---- Form State ----
  const [customerId, setCustomerId] = useState('');
  const [name, setName] = useState('');
  const [planLabel, setPlanLabel] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [createInvoice, setCreateInvoice] = useState(true);
  const [issueInvoice, setIssueInvoice] = useState(false);

  // selected: { [featureId]: { quantity } }
  const [selected, setSelected] = useState({});

  const [submitError, setSubmitError] = useState(null);

  // ---- Live Pricing (client-side) ----
  const lines = useMemo(() => {
    const all = features.data || [];
    return Object.entries(selected).map(([fid, sel]) => {
      const f = all.find(x => x.id === fid);
      if (!f) return null;
      const qty = Math.max(1, parseInt(sel.quantity, 10) || 1);
      return {
        feature: f,
        quantity: qty,
        cents: priceFor(f, qty),
        billing_period: f.billing_period
      };
    }).filter(Boolean);
  }, [selected, features.data]);

  const totals = useMemo(() => {
    const t = { one_time: 0, monthly: 0, yearly: 0 };
    for (const l of lines) {
      if (t[l.billing_period] != null) t[l.billing_period] += l.cents;
    }
    return t;
  }, [lines]);

  const billableCount = lines.filter(l => l.cents > 0).length;

  // ---- Effective features des gewaehlten Customers ----
  // Bundle-Children sichtbar wenn ihr Master bereits in einer bestehenden
  // License des Customers aktiv ist (oder gerade im UI angewaehlt wird).
  const customerEffective = useQuery({
    queryKey: ['customer-effective-features', customerId],
    queryFn: () => api.get(`/licenses/by-customer/${customerId}/effective-features`).then(r => r.data),
    enabled: !!customerId,
    staleTime: 30_000
  });

  const customerEffectiveKeys = useMemo(() => {
    const features = customerEffective.data?.features || {};
    return new Set(Object.keys(features));
  }, [customerEffective.data]);

  // ---- Bundle-Resolution ----
  // Muss VOR toggleFeature stehen damit der De-Select-Cascade-Lookup darauf
  // zugreifen kann.
  const { bundles, unbundled } = useMemo(() => {
    const all = (features.data || []).filter(f => f.is_active);
    return groupFeaturesIntoBundles(all);
  }, [features.data]);

  // ---- Feature toggle / quantity setter ----
  // Bei De-Selektion eines Bundle-Masters werden alle zugehoerigen Children
  // automatisch mit-deselektiert. Beim Aktivieren eines Childs ohne Master
  // greifen wir nicht ein — der User sieht den Child-Row eh nur wenn der
  // Master selektiert ist.
  const toggleFeature = (f) => {
    setSelected(s => {
      const copy = { ...s };
      if (copy[f.id]) {
        delete copy[f.id];
        const bundle = bundles.find(b => b.master.id === f.id);
        if (bundle) {
          for (const child of bundle.children) delete copy[child.id];
        }
      } else {
        copy[f.id] = { quantity: defaultQty(f) };
      }
      return copy;
    });
  };
  const setQty = (fid, q) => {
    setSelected(s => ({ ...s, [fid]: { ...s[fid], quantity: q } }));
  };

  // ---- Submit ----
  const submit = useMutation({
    mutationFn: () => {
      const items = Object.entries(selected).map(([feature_id, sel]) => ({
        feature_id,
        quantity: Math.max(1, parseInt(sel.quantity, 10) || 1)
      }));
      const body = {
        customer_id: customerId,
        name,
        plan_label: planLabel || null,
        valid_until: validUntil ? new Date(validUntil).toISOString() : undefined,
        note: note || null,
        items,
        create_invoice: createInvoice,
        issue_invoice: createInvoice && issueInvoice
      };
      return api.post('/licenses/configure', body).then(r => r.data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['licenses'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['finance-summary'] });
      // Direkt zur neuen Lizenz springen
      navigate(`/licenses/${data.license.id}`, {
        state: { configuratorResult: data }
      });
    },
    onError: (e) => setSubmitError(
      e.response?.data?.message
      || (e.response?.data?.details && JSON.stringify(e.response.data.details))
      || e.message
    )
  });

  const canSubmit = !!customerId && !!name && Object.keys(selected).length > 0 && !submit.isLoading;

  // ---- Group unbundled features by category for the picker ----
  // Bundle-Master + ihre Children werden separat unterhalb als eigene
  // Sections gerendert — hier landen nur die "flachen" Features.
  const byCategory = useMemo(() => {
    const groups = {};
    for (const f of unbundled) {
      const key = f.category || '—';
      (groups[key] ||= []).push(f);
    }
    return groups;
  }, [unbundled]);

  if (!auth.can('licenses', 'create')) {
    return <Alert variant="warning">Keine Berechtigung zum Anlegen von Lizenzen.</Alert>;
  }

  return (
    <>
      <PageHeader title="Lizenz-Konfigurator"
        subtitle="Features wählen, Mengen einstellen, Lizenz und Rechnung in einem Schritt anlegen"
        actions={
          <Button variant="outline-secondary" as={Link} to="/licenses">
            <Icon icon="tabler:arrow-left" className="me-1" />Zurück
          </Button>
        } />

      {submitError && <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>{submitError}</Alert>}

      <Row className="g-3">
        {/* ----- Linke Spalte: Form ----- */}
        <Col lg={8}>
          {/* Schritt 1: Kunde + Eckdaten */}
          <Card className="card-flat mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                <Icon icon="tabler:building" className="me-1" />1. Kunde &amp; Lizenz-Eckdaten
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Kunde *</Form.Label>
                    <Form.Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                      <option value="">— wählen —</option>
                      {(customers.data || []).map(c => (
                        <option key={c.id} value={c.id}>{c.customer_number ? `[${c.customer_number}] ` : ''}{c.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Lizenz-Name *</Form.Label>
                    <Form.Control value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="z.B. Produktion FW Urbach" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Plan-Label</Form.Label>
                    <Form.Control value={planLabel} onChange={(e) => setPlanLabel(e.target.value)}
                      placeholder="z.B. Professional" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Gültig bis</Form.Label>
                    <Form.Control type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Notiz</Form.Label>
                    <Form.Control value={note} onChange={(e) => setNote(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Schritt 2: Features */}
          <Card className="card-flat mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                <Icon icon="tabler:adjustments" className="me-1" />2. Features
              </h6>
              {features.isLoading && <div className="text-muted"><Spinner size="sm" /> Lade Katalog…</div>}

              {/* Bundles: Master immer sichtbar.
                  Children sichtbar wenn Master selektiert ODER Master bereits aktiv. */}
              {bundles.map(b => {
                const masterSelected = !!selected[b.master.id];
                const childrenVisible = masterSelected
                  || customerEffectiveKeys.has(b.master.key);
                return (
                  <div key={b.master.id} className="mb-3 border rounded-2 p-2">
                    <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: '0.04em' }}>
                      <Icon icon="tabler:package" className="me-1" />{b.master.name}
                    </div>
                    <Table size="sm" className="mb-0">
                      <tbody>
                        {renderFeatureRow(b.master, selected, setQty, toggleFeature)}
                        {childrenVisible && b.children.map(c => renderFeatureRow(c, selected, setQty, toggleFeature, { isChild: true }))}
                      </tbody>
                    </Table>
                  </div>
                );
              })}

              {/* Unbundled-Features wie bisher nach Kategorie gruppiert. */}
              {Object.entries(byCategory).map(([cat, list]) => (
                <div key={cat} className="mb-3">
                  <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: '0.04em' }}>{cat}</div>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {list.map(f => renderFeatureRow(f, selected, setQty, toggleFeature))}
                    </tbody>
                  </Table>
                </div>
              ))}
              {bundles.length === 0 && Object.keys(byCategory).length === 0 && !features.isLoading && (
                <div className="text-muted">Keine aktiven Features im Katalog. <Link to="/features">Katalog öffnen →</Link></div>
              )}
            </Card.Body>
          </Card>

          {/* Schritt 3: Optionen */}
          <Card className="card-flat mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                <Icon icon="tabler:settings" className="me-1" />3. Rechnungs-Optionen
              </h6>
              <Form.Check id="create-invoice" type="checkbox" checked={createInvoice}
                onChange={(e) => setCreateInvoice(e.target.checked)}
                label={`Rechnung automatisch erstellen (${billableCount} kostenpflichtige${billableCount === 1 ? 's' : ''} Item${billableCount === 1 ? '' : 's'})`}
                disabled={billableCount === 0} />
              <Form.Check id="issue-invoice" type="checkbox" className="mt-2 ms-4" checked={issueInvoice}
                onChange={(e) => setIssueInvoice(e.target.checked)}
                label="Rechnung sofort ausstellen (statt Entwurf)"
                disabled={!createInvoice || billableCount === 0} />
              {billableCount === 0 && Object.keys(selected).length > 0 && (
                <div className="text-muted small mt-2">
                  Alle ausgewählten Features sind kostenlos — es wird keine Rechnung benötigt.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* ----- Rechte Spalte: Summary ----- */}
        <Col lg={4}>
          <div style={{ position: 'sticky', top: '1rem' }}>
            <Card className="card-flat">
              <Card.Body>
                <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                  Zusammenfassung
                </h6>
                {lines.length === 0 ? (
                  <div className="text-muted small">Noch keine Features ausgewählt.</div>
                ) : (
                  <>
                    <Table size="sm" className="mb-3">
                      <tbody>
                        {lines.map(l => (
                          <tr key={l.feature.id}>
                            <td>
                              <div className="small fw-semibold">{l.feature.name}</div>
                              <div className="text-muted small">
                                {l.quantity > 1 ? `${l.quantity} × ` : ''}{PERIOD_LABEL[l.billing_period]}
                              </div>
                            </td>
                            <td className="text-end small">
                              {l.cents > 0 ? fmtMoney(l.cents) : <span className="text-muted">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <hr />
                    <div className="small">
                      {totals.one_time > 0 && (
                        <div className="d-flex justify-content-between">
                          <span>Einmalig:</span>
                          <strong>{fmtMoney(totals.one_time)}</strong>
                        </div>
                      )}
                      {totals.monthly > 0 && (
                        <div className="d-flex justify-content-between mt-1">
                          <span>Monatlich:</span>
                          <strong>{fmtMoney(totals.monthly)}</strong>
                        </div>
                      )}
                      {totals.yearly > 0 && (
                        <div className="d-flex justify-content-between mt-1">
                          <span>Jährlich:</span>
                          <strong>{fmtMoney(totals.yearly)}</strong>
                        </div>
                      )}
                      {totals.one_time === 0 && totals.monthly === 0 && totals.yearly === 0 && (
                        <div className="text-muted">Nur kostenlose Features.</div>
                      )}
                    </div>
                  </>
                )}

                <Button className="w-100 mt-3" disabled={!canSubmit} onClick={() => {
                  if (!confirm(`Lizenz "${name}" für ${customers.data?.find(c=>c.id===customerId)?.name} mit ${Object.keys(selected).length} Feature${Object.keys(selected).length===1?'':'s'} anlegen?`)) return;
                  setSubmitError(null);
                  submit.mutate();
                }}>
                  {submit.isLoading
                    ? <><Spinner size="sm" className="me-2" />Lege an…</>
                    : <><Icon icon="tabler:check" className="me-1" />Lizenz anlegen</>}
                </Button>

                {!customerId && <div className="text-muted small mt-2">→ Kunde wählen</div>}
                {customerId && !name && <div className="text-muted small mt-2">→ Lizenz-Namen vergeben</div>}
                {customerId && name && Object.keys(selected).length === 0 && (
                  <div className="text-muted small mt-2">→ Mindestens ein Feature wählen</div>
                )}
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </>
  );
}
